import {
  DollarSign,
  Camera,
  Wand2,
  Users,
  Layers,
  Sparkles,
} from "lucide-react";
import { useI18n } from "@/lib/i18n";

const PAIN_STAT_KEYS = ["problem.stat1", "problem.stat2", "problem.stat3"];

export function Problem() {
  const { t } = useI18n();

  const PAIN_STATS = PAIN_STAT_KEYS.map((k) => ({
    value: t(`${k}.value`),
    label: t(`${k}.label`),
    desc: t(`${k}.desc`),
  }));

  const pains = [
    { icon: DollarSign, titleKey: "problem.p1.title", descKey: "problem.p1.desc" },
    { icon: Users,      titleKey: "problem.p2.title", descKey: "problem.p2.desc" },
    { icon: Camera,     titleKey: "problem.p3.title", descKey: "problem.p3.desc" },
    { icon: Layers,     titleKey: "problem.p6.title", descKey: "problem.p6.desc" },
    { icon: Wand2,      titleKey: "problem.p7.title", descKey: "problem.p7.desc" },
    { icon: Sparkles,   titleKey: "problem.p12.title", descKey: "problem.p12.desc" },
  ];

  return (
    <section className="relative py-24 px-6 overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-[0.15] pointer-events-none" />
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-background to-transparent pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-background to-transparent pointer-events-none" />

      <div className="relative max-w-7xl mx-auto">
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-5 leading-[1.1]">
            {t("problem.title.1")}
            <br />
            <span className="text-gradient-orange">{t("problem.title.2")}</span>
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed">
            {t("problem.subtitle")}
          </p>
        </div>

        {/* Bold pain stats — inspired by the cost of doing nothing */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {PAIN_STATS.map(({ value, label, desc }) => (
            <div
              key={label}
              className="p-8 rounded-3xl border border-border bg-card/40 text-center"
            >
              <div className="text-6xl md:text-7xl font-bold text-gradient-orange mb-2 leading-none">
                {value}
              </div>
              <div className="text-xs font-bold tracking-[0.2em] text-muted-foreground uppercase mb-3">
                {label}
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {pains.map(({ icon: Icon, titleKey, descKey }) => (
            <div
              key={titleKey}
              className="group p-6 rounded-2xl bg-card/60 border border-border hover:border-primary/40 hover:bg-card transition-all duration-300"
            >
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center group-hover:bg-primary/15 transition-colors">
                  <Icon className="w-5 h-5 text-destructive group-hover:text-primary transition-colors" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-bold mb-1.5 leading-tight">{t(titleKey)}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{t(descKey)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-14 text-center">
          <p className="text-lg md:text-xl font-semibold">
            {t("problem.outro.1")}{" "}
            <span className="text-gradient-orange">{t("problem.outro.2")}</span>
          </p>
        </div>
      </div>
    </section>
  );
}
